import React from 'react';
import { Button, Textarea, Badge, Spinner } from '@parsina/framework';
import { Send } from 'lucide-react';

import { C, CT, soft } from '../../constants/theme';
import { useTranslation, MIRROR_ICON } from '../../i18n/I18nProvider.jsx';
import { AiAnswerNote, AiDisclaimer } from '../common/PracticeNotice.jsx';
import { CostChip } from '../common/ParsehCost.jsx';
import { useShortcut } from '../../lib/shortcuts.js';

// The copilot's «پرسش» tab: a chat, 360 pixels wide, beside a note being typed.
//
// ── Why this is not the chat page's component ────────────────────────────
//
// core.md §7.5 says the copilot uses «the same rendering component as the chat
// page (§9) in `dense` mode». That page is being built in parallel by another
// author, in `src/components/chat/`, and it is a full ChatGPT-parity surface:
// a thread sidebar, day dividers, markdown with tables, progressive reveal, an
// «older messages» pager. Importing a half-written module across an ownership
// boundary would couple two agents' work in the worst possible place — the one
// screen where an exception unmounts a note the student is typing.
//
// So this is a deliberately SMALLER surface with the same contract: bubbles in
// student/assistant roles, follow-up chips, a cost chip on the composer, and
// `AiAnswerNote` under every answer. When the chat page lands, `dense` mode
// there can replace this file with no change to the copilot's props — that is
// why nothing in it knows about a thread list or a route.
//
// Plain text, not markdown: an answer rendered into a 360px rail wants the
// paragraph, and the markdown renderer belongs to the chat page's author.

const Bubble = ({ message }) => {
  const isStudent = message.role === 'student';
  return (
    <div
      style={{
        alignSelf: isStudent ? 'flex-end' : 'flex-start',
        maxWidth: '92%',
        background: isStudent ? soft(C.navy, 0.08) : C.card,
        border: `1px solid ${isStudent ? 'transparent' : C.border}`,
        borderRadius: 12,
        padding: '8px 10px',
      }}
      data-testid={`copilot-bubble-${message.role}`}
    >
      <p style={{
        margin: 0, fontSize: 12.5, lineHeight: 1.95, whiteSpace: 'pre-wrap', color: C.textPrimary,
      }}
      >
        {message.content}
      </p>
      {!isStudent && <AiAnswerNote />}
    </div>
  );
};

export default function CompactChat({
  messages = [],
  followUps = [],
  onSend,
  busy = false,
  cost,
  disabled = false,
  disabledReason,
}) {
  const { t } = useTranslation();
  const [text, setText] = React.useState('');
  const boxRef = React.useRef(null);

  const send = React.useCallback(async () => {
    const question = text.trim();
    if (!question || busy || disabled) return;
    setText('');
    await onSend(question);
  }, [text, busy, disabled, onSend]);

  // Ctrl+Enter sends — registered in the ONE registry, so the `?` overlay can
  // list it and so no per-screen window listener exists to leak (core.md §7.0).
  //
  // SCOPED TO THE COMPOSER: the binding stays registered (so the overlay lists
  // it) but only acts while the caret is in this textarea. Without the check a
  // Ctrl+Enter pressed in the SOAP note — a habit from every other editor — would
  // send whatever question happened to be sitting in the composer.
  const composerRef = React.useRef(null);
  useShortcut('ctrl+enter', (e) => {
    const box = composerRef.current;
    if (!box) return;
    const target = e?.target || document.activeElement;
    if (!box.contains(target)) return;
    send();
  }, {
    label: t('visit.shortcuts.send'),
    group: t('visit.shortcuts.group'),
    firesInInput: true,
  });

  React.useEffect(() => {
    // Newest at the bottom, as every chat is.
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages.length, busy]);

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0, flex: 1 }}
      data-testid="copilot-chat"
    >
      <AiDisclaimer />

      <div
        ref={boxRef}
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 4,
        }}
      >
        {!messages.length && (
          <p style={{ fontSize: 12.5, color: CT.mute, lineHeight: 1.9 }}>
            {t('visit.copilot.askEmpty')}
          </p>
        )}
        {messages.map((m) => <Bubble key={m.id} message={m} />)}
        {busy && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: CT.mute }}>
            <Spinner size={14} />
            {t('visit.copilot.thinking')}
          </span>
        )}
      </div>

      {!!followUps.length && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {followUps.map((q) => (
            <Badge
              key={q}
              variant="outline"
              role="button"
              tabIndex={0}
              onClick={() => setText(q)}
              onKeyDown={(e) => { if (e.key === 'Enter') setText(q); }}
              style={{ cursor: 'pointer' }}
              data-testid="copilot-followup"
            >
              {q}
            </Badge>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Textarea
          ref={composerRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          disabled={disabled}
          placeholder={disabled ? disabledReason : t('visit.copilot.placeholder')}
          data-testid="copilot-input"
          aria-label={t('visit.copilot.placeholder')}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button
            size="sm"
            onClick={send}
            disabled={busy || disabled || !text.trim()}
            data-testid="copilot-send"
          >
            <Send size={14} aria-hidden style={{ ...MIRROR_ICON, marginInlineEnd: 6 }} />
            {t('visit.copilot.send')}
          </Button>
          {/* The price of the ANSWER, said before the question is sent. The
              student's own turn is free (core.md §3.5). */}
          <CostChip cost={cost} before />
        </div>
      </div>
    </div>
  );
}
